import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectInletStrainer, selectFieldDescriptions } from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../../functions/useFieldClass";

const InletStrainer = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "inletStrainer";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const { options } = useSelector((state) => state.dropdown);

  /****************Add dropdown options here*************************/

  const manufacturerOptions =
    options?.filter((option) => option.DropdownType === "Strainer Manufacturer").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const typeOptions =
    options?.filter((option) => option.DropdownType === "Strainer Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const storedJobDetails = useSelector(selectInletStrainer);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        manufacturer: jobDetails.Manufacturer || "",
        typeSize: jobDetails.TypeSize || "",
        quantity: jobDetails.Quantity || "",
        inletStrainerComments: jobDetails.InletStrainerComments || "",
        // ...other fields...
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="filtration-condensed-grid">
            <Row className="mb-3 filtration-field-row">
              <Col>
                <Form.Group as={Row} controlId="manufacturer">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Manufacturer")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="manufacturer"
                      value={formData.manufacturer || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Manufacturer", manufacturerOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Manufacturer",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.manufacturer}
                      onChange={handleChange}
                    >
                      <option value="">Select Manufacturer</option>
                      {manufacturerOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.manufacturer,
                        manufacturerOptions
                      ) &&
                        formData.manufacturer && (
                          <option
                            value={formData.manufacturer}
                            style={{ color: "red" }}
                          >
                            *{formData.manufacturer}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="typeSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Type / Size")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Type / Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="typeSize"
                      value={formData.typeSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Type / Size", typeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "TypeSize",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.typeSize}
                      onChange={handleChange}
                    >
                      <option value="">Select Type / Size</option>
                      {typeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.typeSize, typeOptions) &&
                        formData.typeSize && (
                          <option
                            value={formData.typeSize}
                            style={{ color: "red" }}
                          >
                            *{formData.typeSize}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="quantity">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Quantity")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Quantity</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="quantity"
                      value={formData.quantity || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Quantity", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Quantity",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.quantity}
                      onChange={handleChange}
                      placeholder="Enter Quantity"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3 filtration-field-row filtration-comments-row">
              <Col>
                <Form.Group controlId="inletStrainerComments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletStrainerComments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="inletStrainerComments"
                    value={formData.inletStrainerComments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "InletStrainerComments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "InletStrainerComments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.inletStrainerComments}
                    onChange={handleChange}
                    placeholder="Enter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default InletStrainer;
